/**
 * 横向拖拽滚动 Composable
 * 为容器元素添加鼠标拖拽滚动功能
 *
 * @param scrollSpeed - 拖拽滚动速度倍数，默认为 2
 * @returns ref 对象，需要绑定到目标容器元素上
 *
 * @example
 * ```vue
 * <script setup>
 * const containerRef = useDragScroll()
 * </script>
 *
 * <template>
 *   <div ref="containerRef" class="overflow-x-auto">
 *     <!-- 内容 -->
 *   </div>
 * </template>
 * ```
 */
export const useDragScroll = (scrollSpeed: number = 2) => {
  const containerRef = ref<HTMLElement | null>(null)

  onMounted(async () => {
    await nextTick()

    const container = containerRef.value
    if (!container) return

    let isDown = false
    let startX = 0
    let scrollLeft = 0

    const handleMouseDown = (e: MouseEvent) => {
      isDown = true
      container.style.cursor = 'grabbing'
      container.style.userSelect = 'none'
      startX = e.pageX - container.offsetLeft
      scrollLeft = container.scrollLeft
    }

    const handleMouseLeave = () => {
      isDown = false
      container.style.cursor = 'grab'
    }

    const handleMouseUp = () => {
      isDown = false
      container.style.cursor = 'grab'
      container.style.userSelect = 'auto'
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return
      e.preventDefault()
      const x = e.pageX - container.offsetLeft
      const walk = (x - startX) * scrollSpeed
      container.scrollLeft = scrollLeft - walk
    }

    // 设置初始光标样式
    container.style.cursor = 'grab'

    // 添加事件监听器
    container.addEventListener('mousedown', handleMouseDown)
    container.addEventListener('mouseleave', handleMouseLeave)
    container.addEventListener('mouseup', handleMouseUp)
    container.addEventListener('mousemove', handleMouseMove)

    // 清理事件监听器
    onUnmounted(() => {
      container.removeEventListener('mousedown', handleMouseDown)
      container.removeEventListener('mouseleave', handleMouseLeave)
      container.removeEventListener('mouseup', handleMouseUp)
      container.removeEventListener('mousemove', handleMouseMove)
    })
  })

  return containerRef
}
